import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button, Container, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { generateClient } from '@aws-amplify/api';
import { upsertAdCampaign } from '@/graphql/mutations';

const client = generateClient();

const platformOptions = [
  { id: 1, label: 'Google and Microsoft (Bing) ads' },
  { id: 2, label: 'The Trade Desk, DV360, MediaMath, Amazon DSP' },
  { id: 3, label: 'Amazon and Walmart Connect Instacart ads' },
  { id: 4, label: 'Youtube, Spotify, Hulu, and Disney advertisings' },
  { id: 5, label: 'TikTok, LinkedIn, X (formerly Twitter), Snapchat, Pinterest, Reddit ads' },
];

const Campaigns = () => {
  const navigate = useNavigate();
  const initialValues = {
    campaignName: '',
    platforms: [],
    flightStart: '',
    flightEnd: '',
    spend: '',
    budget: '',
    cpl: '',
    cpa: '',
  };

  const validationSchema = Yup.object({
    campaignName: Yup.string().required('Campaign name is required'),
  });

  const formatDate = (dateStr) => new Date(dateStr);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const input = {
        ad_campaigns: {
          id: values.id || undefined,
          name: values.campaignName,
          budget: parseFloat(values.budget),
          spend: parseFloat(values.spend),
          cost_per_action: parseFloat(values.cpa),
          cost_per_lead: parseFloat(values.cpl),
          flight_start_date: formatDate(values.flightStart),
          flight_end_date: formatDate(values.flightEnd),
        }
      };


      const platformIds = values.platforms
        .map((label) => {
          const matched = platformOptions.find((opt) => opt.label === label);
          return matched?.id ? { id: matched.id } : null;
        })
        .filter(Boolean);

      if (platformIds.length > 0) {
        input.ad_campaigns.ad_platforms = platformIds;
      }

      const variables = { input };
      if (values.id) variables.input_delete = [];

      console.log("🧪 Sending input:", JSON.stringify(input, null, 2));

      const response = await client.graphql({
        query: upsertAdCampaign,
        variables,
      });

      console.log(" Campaign saved:", JSON.stringify(response.data.upsertAdCampaign, null, 2));
      alert(" Campaign saved successfully.");
      resetForm();
    } catch (err) {
      console.error(" Error saving campaign:", JSON.stringify(err, null, 2));
      alert(" Failed to save campaign. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid="md" className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4
          className="mb-4 d-flex align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/campaigns')}
        >
          <span className="me-2">&#8592;</span> New ampaigns 
        </h4>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="campaignForm">
            Save Campaign
          </Button>
        </div>
      </div>

      <div className="p-4 rounded-4 shadow-sm bg-white border" style={{ maxWidth: '700px', margin: '0 auto' }}>
        <p className="text-muted mb-4">
          The purple toaster danced gracefully under the moonlight while humming jazz tunes.
        </p>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form id="campaignForm">
              <div className="mb-3">
                <label className="form-label fw-semibold">Campaign Name</label>
                <Field
                  name="campaignName"
                  type="text"
                  placeholder="Start typing..."
                  className="form-control form-control-soft"
                />
                <div className="text-danger small">
                  <ErrorMessage name="campaignName" />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Platform</label>
                <Select
                  isMulti
                  name="platforms"
                  options={platformOptions}
                  value={values.platforms}
                  onChange={(selected) => setFieldValue('platforms', selected)}
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.id}
                  className="form-control-soft"
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Flight Dates</label>
                <div className="d-flex gap-2">
                  <Field name="flightStart" type="date" className="form-control form-control-soft" />
                  <Field name="flightEnd" type="date" className="form-control form-control-soft" />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Spend</label>
                <Field name="spend" type="number" className="form-control form-control-soft" />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Budget</label>
                <Field name="budget" type="number" className="form-control form-control-soft" />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Cost per lead (CPL)</label>
                <Field name="cpl" type="number" className="form-control form-control-soft" />
              </div>

              <div className="mb-4">
                <label className="form-label fw-semibold">Cost per action (CPA)</label>
                <Field name="cpa" type="number" className="form-control form-control-soft" />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Container>
  );
};

export default Campaigns;
